import { useEffect, useRef, useState } from 'react';

let googleMapsLoader;

function loadGoogleMaps(apiKey) {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (googleMapsLoader) return googleMapsLoader;
  googleMapsLoader = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=geometry`;
    script.async = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = () => reject(new Error('No se pudo cargar Google Maps.'));
    document.head.appendChild(script);
  });
  return googleMapsLoader;
}

// Coordinate-only drawing used when Google Maps cannot be loaded in the browser.
export function RouteSchematic({ route, className = 'h-[280px]' }) {
  const points = [route.origen, ...route.paradas]
    .map((item) => ({ lat: Number(item.latitud), lng: Number(item.longitud) }))
    .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
  if (!points.length) return null;
  const lats = points.map((point) => point.lat);
  const lngs = points.map((point) => point.lng);
  const [minLat, maxLat, minLng, maxLng] = [Math.min(...lats), Math.max(...lats), Math.min(...lngs), Math.max(...lngs)];
  const span = Math.max(maxLat - minLat, maxLng - minLng, 0.002);
  const project = (point) => ({
    x: 30 + ((point.lng - minLng) / span) * 340,
    y: 270 - ((point.lat - minLat) / span) * 240,
  });
  const projected = points.map(project);
  const path = projected.map((point, index) => `${index ? 'L' : 'M'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(' ');

  return (
    <div className={`relative w-full overflow-hidden rounded-xl border border-[rgba(var(--border),0.3)] bg-[rgba(var(--subpanel),0.5)] ${className}`}>
      <svg viewBox="0 0 400 300" preserveAspectRatio="xMidYMid meet" className="h-full w-full" aria-label="Esquema del recorrido">
        <path d={path} fill="none" stroke="rgb(var(--brand))" strokeWidth="3" strokeDasharray="7 5" strokeLinejoin="round" />
        {projected.map((point, index) => (
          <g key={index}>
            <circle cx={point.x} cy={point.y} r={index ? 11 : 13} fill={index ? 'rgb(var(--brand))' : 'rgb(var(--fg))'} stroke="rgb(var(--panel))" strokeWidth="2.5" />
            <text x={point.x} y={point.y + 4} textAnchor="middle" fontSize="11" fontWeight="700" fill="rgb(var(--panel))">{index ? index : 'S'}</text>
          </g>
        ))}
      </svg>
      <div className="absolute bottom-2 left-2 rounded-lg bg-[rgba(var(--panel),0.9)] px-2 py-1 text-[10px] text-[rgba(var(--fg),0.62)]">Esquema sin mapa de calles · S = sucursal</div>
    </div>
  );
}

export default function GoogleRouteMap({ route, className = 'h-[280px]' }) {
  const containerRef = useRef(null);
  const [error, setError] = useState('');
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_BROWSER_API_KEY;

  useEffect(() => {
    if (!route || !containerRef.current || !apiKey) return undefined;
    let cancelled = false;
    loadGoogleMaps(apiKey)
      .then((maps) => {
        if (cancelled || !containerRef.current) return;
        const origin = { lat: Number(route.origen.latitud), lng: Number(route.origen.longitud) };
        const map = new maps.Map(containerRef.current, { center: origin, zoom: 12, mapTypeControl: false, streetViewControl: false });
        const bounds = new maps.LatLngBounds(origin);
        new maps.Marker({ map, position: origin, label: 'S', title: route.origen.nombre });
        route.paradas.forEach((stop) => {
          const position = { lat: Number(stop.latitud), lng: Number(stop.longitud) };
          bounds.extend(position);
          new maps.Marker({ map, position, label: String(stop.orden), title: `${stop.numeroGuia} - ${stop.destinatario}` });
        });
        if (route.polyline) {
          new maps.Polyline({ map, path: maps.geometry.encoding.decodePath(route.polyline), strokeColor: '#1867c0', strokeOpacity: 0.9, strokeWeight: 5 });
        }
        if (route.paradas.length) map.fitBounds(bounds, 48);
      })
      .catch((loadError) => setError(loadError.message));
    return () => { cancelled = true; };
  }, [apiKey, route]);

  if (!apiKey || error) return <RouteSchematic route={route} className={className} />;
  return <div ref={containerRef} className={`${className} w-full overflow-hidden rounded-xl`} aria-label="Mapa del recorrido" />;
}
